import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import loyaltyService from '../../../services/loyaltyService';

const EarningOpportunities = ({ user, onTokensEarned }) => {
  const [earningRules, setEarningRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRule, setExpandedRule] = useState(null);

  // Load earning rules
  useEffect(() => {
    const loadEarningRules = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const { data, error } = await loyaltyService?.getEarningRules();
        
        if (error) {
          setError(error?.message || 'Failed to load earning opportunities');
        } else {
          setEarningRules(data || []);
        }
      } catch (err) {
        setError('Failed to connect to earning service');
      } finally {
        setLoading(false);
      }
    };

    loadEarningRules();
  }, []);

  // Get rule icon
  const getRuleIcon = (triggerType) => {
    const icons = {
      'activity_complete': 'Activity',
      'goal_achieved': 'Target',
      'streak_milestone': 'Flame',
      'daily_login': 'Calendar',
      'community_action': 'Users',
      'challenge_complete': 'Trophy',
      'referral_signup': 'UserPlus'
    };
    return icons?.[triggerType] || 'Plus';
  };

  // Get rule color
  const getRuleColor = (triggerType) => {
    const colors = {
      'activity_complete': 'bg-green-100 text-green-800',
      'goal_achieved': 'bg-blue-100 text-blue-800',
      'streak_milestone': 'bg-orange-100 text-orange-800',
      'daily_login': 'bg-purple-100 text-purple-800',
      'community_action': 'bg-pink-100 text-pink-800',
      'challenge_complete': 'bg-yellow-100 text-yellow-800',
      'referral_signup': 'bg-indigo-100 text-indigo-800'
    };
    return colors?.[triggerType] || 'bg-gray-100 text-gray-800';
  };

  // Get rule title
  const getRuleTitle = (triggerType) => {
    const titles = {
      'activity_complete': 'Complete Activities',
      'goal_achieved': 'Achieve Goals',
      'streak_milestone': 'Maintain Streaks',
      'daily_login': 'Daily Check-in',
      'community_action': 'Community Participation',
      'challenge_complete': 'Complete Challenges',
      'referral_signup': 'Refer Friends'
    };
    return titles?.[triggerType] || triggerType?.replace('_', ' ');
  };

  // Mock earning opportunities with action buttons
  const mockOpportunities = [
    {
      id: 'quick-activity',
      title: 'Log a Quick Activity',
      description: 'Record any wellness activity you completed today',
      tokens: 10,
      icon: 'Plus',
      color: 'bg-primary',
      action: 'log_activity',
      available: true
    },
    {
      id: 'daily-checkin',
      title: 'Daily Check-in',
      description: 'Mark your daily wellness check-in',
      tokens: 5,
      icon: 'CheckCircle',
      color: 'bg-success',
      action: 'daily_checkin',
      available: true
    },
    {
      id: 'community-post',
      title: 'Share in Community',
      description: 'Post about your wellness journey in the forums',
      tokens: 8,
      icon: 'MessageCircle',
      color: 'bg-accent',
      action: 'community_post',
      available: true
    },
    {
      id: 'goal-progress',
      title: 'Update Goal Progress',
      description: 'Update progress on your active health goals',
      tokens: 15,
      icon: 'Target',
      color: 'bg-secondary',
      action: 'update_goal',
      available: true
    }
  ];

  // Handle quick action
  const handleQuickAction = async (opportunity) => {
    if (!opportunity?.available) return;

    try {
      // Simulate earning tokens for the action
      const mockTransaction = {
        amount: opportunity?.tokens,
        description: `Earned tokens for: ${opportunity?.title}`
      };

      // In a real app, this would make an API call to record the action
      // For now, we'll just show a success message
      alert(`Great! You earned ${opportunity?.tokens} tokens for ${opportunity?.title}`);
      
      // Call the parent callback to update the wallet
      onTokensEarned?.(opportunity?.tokens, opportunity?.title);
      
    } catch (error) {
      alert('Failed to complete action. Please try again.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-xl font-semibold text-foreground mb-2">Ways to Earn Tokens</h2>
        <p className="text-muted-foreground">
          Complete these activities to earn loyalty tokens and unlock rewards
        </p>
      </div>
      {/* Quick Actions */}
      <div>
        <h3 className="text-lg font-medium text-foreground mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {mockOpportunities?.map((opportunity) => (
            <div
              key={opportunity?.id}
              className="bg-card rounded-lg border border-border p-4 shadow-wellness hover:shadow-lg transition-shadow"
            >
              <div className="flex items-start space-x-3">
                <div className={`w-10 h-10 ${opportunity?.color} rounded-full flex items-center justify-center flex-shrink-0`}>
                  <Icon name={opportunity?.icon} size={20} color="white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-foreground mb-1">{opportunity?.title}</h4>
                  <p className="text-sm text-muted-foreground mb-3">{opportunity?.description}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-1">
                      <Icon name="Coins" size={14} className="text-primary" />
                      <span className="text-sm font-medium text-primary">
                        +{loyaltyService?.formatTokens(opportunity?.tokens)} tokens
                      </span>
                    </div>
                    <button
                      onClick={() => handleQuickAction(opportunity)}
                      disabled={!opportunity?.available}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                        opportunity?.available
                          ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                          : 'bg-muted text-muted-foreground cursor-not-allowed'
                      }`}
                    >
                      {opportunity?.available ? 'Earn Now' : 'Completed'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Earning Rules */}
      <div>
        <h3 className="text-lg font-medium text-foreground mb-4">Earning Rules</h3>
        
        {loading ? (
          <div className="space-y-4">
            {[...Array(5)]?.map((_, i) => (
              <div key={i} className="bg-card rounded-lg border border-border p-4 animate-pulse">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-muted rounded-full"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-muted rounded w-2/3"></div>
                    <div className="h-3 bg-muted rounded w-full"></div>
                  </div>
                  <div className="w-20 h-6 bg-muted rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center space-x-3">
            <Icon name="AlertTriangle" size={20} className="text-destructive" />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        ) : earningRules?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Plus" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">No earning rules available</p>
          </div>
        ) : (
          <div className="space-y-3">
            {earningRules?.map((rule) => (
              <div key={rule?.id} className="bg-card rounded-lg border border-border shadow-wellness">
                <div 
                  className="p-4 cursor-pointer hover:bg-muted/30 transition-colors"
                  onClick={() => setExpandedRule(expandedRule === rule?.id ? null : rule?.id)}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center">
                      <Icon name={getRuleIcon(rule?.trigger_type)} size={20} className="text-muted-foreground" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-medium text-foreground">{rule?.rule_name}</h4>
                        <div className="flex items-center space-x-2">
                          <div className="flex items-center space-x-1">
                            <Icon name="Coins" size={14} className="text-primary" />
                            <span className="font-medium text-primary">
                              +{loyaltyService?.formatTokens(rule?.tokens_per_trigger)}
                            </span>
                          </div>
                          <Icon 
                            name={expandedRule === rule?.id ? 'ChevronUp' : 'ChevronDown'} 
                            size={16} 
                            className="text-muted-foreground" 
                          />
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-muted-foreground truncate">
                          {rule?.description}
                        </p>
                        <div className={`px-2 py-1 rounded-full text-xs font-medium ${getRuleColor(rule?.trigger_type)}`}>
                          {getRuleTitle(rule?.trigger_type)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedRule === rule?.id && (
                  <div className="px-4 pb-4 border-t border-border">
                    <div className="pt-4 space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {rule?.max_per_day && (
                          <div className="text-center p-3 bg-muted/50 rounded-lg">
                            <p className="text-lg font-semibold text-foreground">{rule?.max_per_day}</p>
                            <p className="text-xs text-muted-foreground">Daily Limit</p>
                          </div>
                        )}
                        {rule?.max_per_week && (
                          <div className="text-center p-3 bg-muted/50 rounded-lg">
                            <p className="text-lg font-semibold text-foreground">{rule?.max_per_week}</p>
                            <p className="text-xs text-muted-foreground">Weekly Limit</p>
                          </div>
                        )}
                        <div className="text-center p-3 bg-primary/10 rounded-lg">
                          <p className="text-lg font-semibold text-primary">{rule?.tokens_per_trigger}</p>
                          <p className="text-xs text-muted-foreground">Tokens per Action</p>
                        </div>
                      </div>

                      {/* Conditions */}
                      {rule?.conditions && Object.keys(rule?.conditions)?.length > 0 && (
                        <div>
                          <h5 className="text-sm font-medium text-foreground mb-2">Conditions:</h5>
                          <div className="space-y-1">
                            {rule?.conditions?.min_duration_minutes && (
                              <p className="text-sm text-muted-foreground">
                                • Minimum {rule?.conditions?.min_duration_minutes} minutes duration
                              </p>
                            )}
                            {rule?.conditions?.bonus_multiplier && (
                              <p className="text-sm text-muted-foreground">
                                • Bonus multiplier: {rule?.conditions?.bonus_multiplier}x for qualifying activities
                              </p>
                            )}
                            {rule?.conditions?.min_characters && (
                              <p className="text-sm text-muted-foreground">
                                • Minimum {rule?.conditions?.min_characters} characters required
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                      
                      <div className="flex justify-center pt-2">
                        <button
                          onClick={(e) => {
                            e?.stopPropagation();
                            // This would navigate to the relevant section
                            console.log('Navigate to:', rule?.trigger_type);
                          }}
                          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                        >
                          Start Earning
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Tips Section */}
      <div className="bg-primary/5 rounded-lg p-6 border border-primary/20">
        <div className="flex items-start space-x-3">
          <Icon name="Lightbulb" size={24} className="text-primary flex-shrink-0 mt-1" />
          <div>
            <h3 className="font-medium text-foreground mb-2">Pro Tips for Earning More Tokens</h3>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li>• Complete multiple activities per day to maximize your daily earning potential</li>
              <li>• Set and achieve health goals for bonus token rewards</li>
              <li>• Maintain activity streaks for milestone bonuses</li>
              <li>• Participate in community discussions and challenges</li>
              <li>• Check in daily to earn consistent login bonuses</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EarningOpportunities;